import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PortalManagementPackagesService } from 'src/app/features/portal/_services/management/portal-management-packages.service';
import { PortalManagementSubscriptionService } from 'src/app/features/portal/_services/management/portal-management-subscription.service';
import { UserService } from 'src/app/_share/_services/user.service';

/**
 * Staff-only: adds a subscription (a new line) to an existing customer.
 * Unlike the customer self-service signup flow, the backend endpoint this
 * calls (POST /management/subscriptions/create) allows a customer who
 * already has a subscription to get another one on the same account -
 * for a genuine multi-line customer, not a signup error.
 */
@Component({
  selector: 'app-add-subscription-dialog',
  templateUrl: './add-subscription-dialog.component.html',
  styleUrls: ['./add-subscription-dialog.component.scss']
})
export class AddSubscriptionDialogComponent implements OnInit {
  products: Array<{ label: string; value: number }> = [];
  isLoadingProducts = false;

  // "eft" is the stored value for a bank debit: SEPA Direct Debit on the
  // Irish portal, EFT / bank transfer elsewhere. Label it per portal.
  paymentMethodOptions = [
    { label: 'Credit Card', value: 'creditcard' },
    { label: 'EFT / Bank Transfer', value: 'eft' },
    { label: 'Cash', value: 'cash' }
  ];

  formAddSubscription: FormGroup = this.fb.group({
    portal_product_id: [null, Validators.required],
    preferred_payment_method: ['creditcard', Validators.required],
    billing_auto_renew: [true],
    promotion_code: [''],
    referral_code: [''],
    device_reference: ['']
  });

  submitting = false;
  errorMessage = '';

  constructor(
    private fb: FormBuilder,
    private pmss: PortalManagementSubscriptionService,
    private pmps: PortalManagementPackagesService,
    public config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    public messageService: MessageService,
    private userService: UserService
  ) {
    this.userService.currentUser$.subscribe((user: any) => {
      const portal = (user?.portal_tenants || []).find((t: any) => t.id === user?.portal_tenant_id);
      const name = String(portal?.name || '').toLowerCase();
      if (name.includes('scie') || name.includes(' ie')) {
        this.paymentMethodOptions = this.paymentMethodOptions.map((o) =>
          o.value === 'eft' ? { ...o, label: 'SEPA Direct Debit' } : o
        );
      }
    });
  }

  ngOnInit(): void {
    this.isLoadingProducts = true;
    this.pmps.getPortalPackagesManagement().subscribe({
      next: (products: any[]) => {
        this.products = (products || [])
          .filter((p) => p.available !== false)
          .map((p) => ({ label: p.name, value: p.id }));
        this.isLoadingProducts = false;
      },
      error: () => {
        this.isLoadingProducts = false;
      }
    });
  }

  get f() {
    return this.formAddSubscription.controls;
  }

  onAddSubscriptionSubmit(): void {
    this.errorMessage = '';

    if (this.formAddSubscription.invalid) {
      this.formAddSubscription.markAllAsTouched();
      return;
    }

    const portalEndCustomerId = this.config.data?.portalEndCustomerId;
    if (!portalEndCustomerId) {
      this.errorMessage = 'No customer selected - please close and try again.';
      return;
    }

    const formValue = this.formAddSubscription.value;
    const payload = {
      portal_end_customer_id: portalEndCustomerId,
      portal_product_id: formValue.portal_product_id,
      preferred_payment_method: formValue.preferred_payment_method,
      billing_auto_renew: formValue.billing_auto_renew,
      promotion_code: formValue.promotion_code || null,
      referral_code: formValue.referral_code || null,
      device_reference: formValue.device_reference || ''
    };

    this.submitting = true;
    this.pmss.createSubscription(payload).subscribe({
      next: (res) => {
        this.submitting = false;
        const claimed = !!res?.data?.subscription_activated && !!formValue.device_reference;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: claimed
            ? `Subscription created and linked to the existing circuit (now ${res.data.subscription_account_reference}).`
            : 'Subscription created successfully.'
        });
        this.ref.close(res);
      },
      error: (error) => {
        this.submitting = false;
        this.errorMessage =
          error?.error?.detail || 'Failed to create subscription. Please try again.';
      }
    });
  }
}
